import { useEffect, useContext, useMemo, useCallback, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  useMediaQuery
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import IconSwitch from "../atoms/IconSwitch"
import { useTheme } from "@mui/material/styles"
import { useNavigate, useLocation } from 'react-router-dom'
import { DataGrid } from "@mui/x-data-grid"
import { TopBarContext } from "../../providers/TopBarProvider"
import { ToastContext } from "../../providers/ToastProvider"
import RoleService from "../../services/RoleService"
import CustomNoRows from '../atoms/CustomNoRows'
import RoleCreator from '../molecules/RoleCreator'

const Role = () => {

  const [ permissions, setPermissions ] = useState([])
  const [ rolePermissions, setRolePermissions ] = useState([])
  const [ role, setRole ] = useState({
    displayName: "Cargando...",
    description: "",
    grantedPermissions: [],
    loaded: false,
    edit: false
  })
  const [ error, setError ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ openModal, setOpenModal ] = useState(false)
  const [ expandedCategories, setExpandedCategories ] = useState({})

  // Función para categorizar permisos
  const categorizePermission = (permissionName) => {
    const name = permissionName.toLowerCase()
    
    if (name.includes('client')) return 'Clientes'
    if (name.includes('order')) return 'Órdenes'
    if (name.includes('product')) return 'Productos'
    if (name.includes('line') || name.includes('subline')) return 'Líneas y Sublíneas'
    if (name.includes('color') || name.includes('size')) return 'Colores y Tallas'
    if (name.includes('stock')) return 'Stock'
    if (name.includes('pricelist')) return 'Listas de Precios'
    if (name.includes('user')) return 'Usuarios'
    if (name.includes('role')) return 'Roles'
    if (name.includes('package') || name.includes('dispatch')) return 'Despachos y Paquetes'
    
    return 'Otros'
  }

  // Función para identificar si un permiso es el master de su categoría
  const isMasterPermission = (permissionName, category) => {
    const name = permissionName.toLowerCase()
    const cat = category.toLowerCase()

    // Mapeo de categorías a su palabra clave
    const categoryKeywords = {
      'clientes': ['clients', 'clientbranches'],
      'órdenes': 'orders',
      'productos': 'products',
      'líneas y sublíneas': ['lines', 'sublines'],
      'colores y tallas': ['colors', 'sizes'],
      'stock': 'stock',
      'listas de precios': 'pricelist',
      'usuarios': 'users',
      'roles': 'roles',
      'despachos y paquetes': ['packages', 'dispatches'],
    }

    const keyword = categoryKeywords[cat]
    
    if (Array.isArray(keyword)) {
      // Para categorías con múltiples palabras clave, buscar el que más se parezca
      return keyword.some(k => name === `pages.${k}`)
    }
    
    // El permiso master es el que tiene exactamente el formato Pages_[Category]
    return name === `pages.${keyword}`
  }

  // Verificar si el permiso master de una categoría está activo
  const isMasterPermissionActive = useCallback((category) => {
    const categoryPermissions = permissions.filter(p => !p.isCategory && p.category === category)
    const masterPerm = categoryPermissions.find(p => p.isMaster)
    
    if (!masterPerm) return true // Si no hay master, permitir todos
    
    return rolePermissions.includes(masterPerm.name)
  }, [permissions, rolePermissions])

  // Función para alternar expansión de categorías
  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }, [])

  const onChangeSwitch = useCallback((newPer, value) => {
    
    setRolePermissions(oldRolePermissions => {
      if (value) {
        // Agregar el permiso
        return [...oldRolePermissions, newPer]
      } else {
        // Remover el permiso
        return oldRolePermissions.filter(per => per !== newPer)
      }
    })

  }, [])

  const { setTitle, setSearchOptions, setBtnPrimary } = useContext(TopBarContext)
  const { setShowToast, setToastMessage } = useContext(ToastContext)
  const roleService = useMemo(()=>new RoleService(), [])
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const bpMdDown = useMediaQuery(theme.breakpoints.down("md"))

  const updatePermissions = useCallback(async () => {

    try {
      
      setIsLoading(true)

      const payload = {
        ...role,
        grantedPermissions: [...rolePermissions]
      }

      const res = await roleService.update(payload)

      if (!res.data.success) {
        return
      }

      setRole(oldRole=>({
        ...oldRole,
        edit: true,
        grantedPermissions: [...rolePermissions]
      }))

    } catch (error) {
      setToastMessage([
        {
          severity: "error",
          primary: `Error al intentar actualizar los permisos`
        }
      ])
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }

  }, [role, rolePermissions, roleService, setToastMessage, setShowToast])

  const columns = useMemo(() => [
    { 
      field: 'displayName',
      headerName: 'Permisos',
      minWidth: 200,
      flex: 1,
      sorting: false,
      renderCell: (item) => {
        // Si es una fila de categoría (grupo)
        if (item.row.isCategory) {
          const isExpanded = expandedCategories[item.row.displayName]
          return (
            <Box 
              onClick={() => toggleCategory(item.row.displayName)}
              sx={{ 
                fontWeight: 700, 
                fontSize: "1.15rem",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
                width: '100%',
                pr: 2,
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  component={FontAwesomeIcon} 
                  icon={isExpanded ? "chevron-down" : "chevron-right"} 
                  sx={{fontSize: "1rem", mr: 2, transition: 'transform 0.2s'}} 
                />
                <Box component={FontAwesomeIcon} icon={isExpanded ? "folder-open" : "folder"}  sx={{fontSize: "1.2rem", mr: 2, color: "primary.main",}} />
                {item.row.displayName}
              </Box>
            </Box>
          )
        }
        // Si es un permiso normal
        const isMaster = item.row.isMaster
        return (
          <Box sx={{ pl: 4, display: 'flex', alignItems: 'center' }}>
            <Box 
              component={FontAwesomeIcon} 
              icon={isMaster ? "shield-halved" : "key"} 
              color={isMaster ? "warning.main" : "secondary.main"} 
              sx={{fontSize: isMaster ? "1.1rem" : "1rem", mr: 2}} 
            />
            {item.row.displayName}
            {isMaster && (
              <Box 
                component="span" 
                sx={{ 
                  ml: 1.5, 
                  fontSize: "0.75rem", 
                  fontWeight: 600,
                  color: "white",
                  backgroundColor: "warning.light",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1
                }}
              >
                MASTER
              </Box>
            )}
          </Box>
        )
      }
    },
    {
      field: 'edit',
      headerName: '',
      sortable: false,
      width: 135,
      headerAlign: 'center',
      align:'center',
      renderHeader: () => <Button
        variant="text" 
        size="small"
        color='success'
        onClick={updatePermissions}
        disabled={(!role.loaded) || (role.grantedPermissions.length === rolePermissions.length && role.grantedPermissions.findIndex(per => !rolePermissions.includes(per)) === -1)}
      >
        <Box component={FontAwesomeIcon} icon="rotate" mr={1} /> Actualizar
      </Button>,
      renderCell: (item) => {
        // Si es una fila de categoría, no mostrar switch
        if (item.row.isCategory) {
          return null
        }

        const isChecked = rolePermissions.includes(item.row.name)
        const isMaster = item.row.isMaster
        const masterActive = isMasterPermissionActive(item.row.category)
        
        // Deshabilitar si no es master y el master está desactivado
        const isDisabled = !isMaster && !masterActive

        return <IconSwitch 
          key={`${item.row.name}-${isChecked}-${isDisabled}`}
          checked={isChecked}
          onChange={(_, value)=>onChangeSwitch(item.row.name, value)} 
          color="success"
          disabled={isDisabled}
        />
      }
    }
  ], [expandedCategories, rolePermissions, role.loaded, role.grantedPermissions, isMasterPermissionActive, updatePermissions, toggleCategory, onChangeSwitch]);

  const getAllPermissions = useCallback(async() => {

    try {
      setIsLoading(true)
      const res = await roleService.getAllPermissions()
      
      if (!res.data.success) {
        return
      }

      // Agrupar permisos por categoría
      const permissionsByCategory = {}
      
      res.data.result.items.forEach((item) => {
        const category = categorizePermission(item.name)
        if (!permissionsByCategory[category]) {
          permissionsByCategory[category] = []
        }
        permissionsByCategory[category].push(item)
      })

      // Crear array con categorías y permisos
      const structuredPermissions = []
      let idCounter = 0
      
      // Ordenar categorías alfabéticamente
      const sortedCategories = Object.keys(permissionsByCategory).sort()
      
      // Inicializar todas las categorías como colapsadas
      const initialExpandedState = {}
      sortedCategories.forEach(category => {
        initialExpandedState[category] = false
      })
      setExpandedCategories(initialExpandedState)
      
      sortedCategories.forEach((category) => {
        // Agregar fila de categoría
        structuredPermissions.push({
          id: idCounter++,
          displayName: category,
          name: `category_${category}`,
          isCategory: true
        })
        
        // Marcar permisos master y ordenar (master primero)
        const categoryPermissions = permissionsByCategory[category].map(permission => ({
          ...permission,
          isMaster: isMasterPermission(permission.name, category),
          category: category
        }))
        
        // Ordenar: permisos master primero, luego los demás
        categoryPermissions.sort((a, b) => {
          if (a.isMaster && !b.isMaster) return -1
          if (!a.isMaster && b.isMaster) return 1
          return 0
        })
        
        // Agregar permisos ordenados de la categoría
        categoryPermissions.forEach((permission) => {
          structuredPermissions.push({
            ...permission,
            id: idCounter++
          })
        })
      })

      setPermissions(structuredPermissions)

    } catch {
      setError(true)
    } finally {
      setIsLoading(false)
    }

  }, [roleService, setPermissions])

  // Filtrar permisos según categorías expandidas
  const visiblePermissions = useMemo(() => {
    return permissions.filter(permission => {
      // Siempre mostrar las filas de categoría
      if (permission.isCategory) return true
      // Mostrar permisos solo si su categoría está expandida
      return expandedCategories[permission.category]
    })
  }, [permissions, expandedCategories])

  const getRole = useCallback(async() => {
    const id = Number(location.search.split("=")[1])
    setRole(oldRole=>({
      ...oldRole,
      ...location.state
    }))

    setIsLoading(true)

    try {
      
      const res = await roleService.get(id)

      if (!res.data.success) {
        return
      }

      setRole(oldRole=>({
        ...oldRole,
        loaded: true,
        ...res.data.result
      }))

      setRolePermissions([...res.data.result.grantedPermissions])

    } catch {
      setToastMessage([
        {
          severity: "error",
          primary: `Error al intentar obtener los datos del rol`
        }
      ])
      setShowToast(true)
      navigate("/roles")
    }

  },[location, navigate, roleService, setRole, setToastMessage, setShowToast])

  useEffect(()=>{
    getRole()
  }, [getRole])

  useEffect(()=>{
    if (role.loaded) {
      getAllPermissions()
    }
  },[role, getAllPermissions])

  useEffect(()=>{
    setTitle(role.displayName)
    setSearchOptions({
      action: null
    })
    setBtnPrimary({
      label: "Editar rol",
      action: () => setOpenModal(true),
      icon: "pencil"
    })
  },[role, setTitle, setSearchOptions, setBtnPrimary])

  const classes = {
    root: {
      maxWidth: "md",
      ml: bpMdDown ? 0 : 8,
    },
    datagrid: {
      marginTop: 5,
      fontSize: "1.1rem",
      height: 400,
      ".MuiDataGrid-cell:focus,.MuiDataGrid-cell:focus-within": {
        outline: "none"
      },
      "& .category-row": {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
        cursor: "default",
        "&:hover": {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
        }
      },
      "& .permission-row": {
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        }
      }
    }
  }

return <>
    <Box sx={classes.root}>
      <Typography component="p" ml={bpMdDown ? 9 : 1}>
        {role.description}
      </Typography>
      <Paper elevation={2}>
        <DataGrid
          sx={classes.datagrid}
          columns={columns}
          rows={visiblePermissions}
          hideFooter={true}
          autoHeight={visiblePermissions.length > 0}
          disableColumnMenu
          disableRowSelectionOnClick
          loading={isLoading}
          getRowClassName={(params) => params.row.isCategory ? 'category-row' : 'permission-row'}
          isRowSelectable={(params) => !params.row.isCategory}
          slots={{
            noRowsOverlay: ()=><CustomNoRows message={error ? "Error obteniendo los permisos" : "No hay permisos registrados"} />,
            noResultsOverlay: ()=><CustomNoRows message={error ? "Error obteniendo obtener los permisos" : "No hay permisos registrados"} />
          }}
        />
      </Paper>
    </Box>
    <RoleCreator 
      open={openModal}
      handleClose={()=>setOpenModal(false)}
      service={roleService}
      setRoles={setRole}
      editRole={{
        ...role,
        edit: true
      }}
    />
  </>
}

export default Role